import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { moduleService, progressService } from '../../services'
import { useAuth } from '../../auth/auth.context'
import { useLocale } from '../../hooks/use-locale'
import PageLayout from '../../components/layout/page-layout'
import { Button } from '../../components/ui/button'
import { Container } from '../../components/ui/container'
import { Badge } from '../../components/ui/badge'
import { ButtonBig } from '../../components/buttons/button-big'
import { ChartNoAxesColumnIncreasing, Clock, User, Video, UserPlus } from 'lucide-react'

export default function ModuleDetail() {
  const { t, i18n } = useTranslation()
  const { formatDate, formatTime } = useLocale()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const isWeiterbildung = location.pathname.startsWith('/weiterbildung')
  const basePath = isWeiterbildung ? '/weiterbildung' : '/modules'
  const typeLabel = isWeiterbildung
    ? t('common.typeWeiterbildung')
    : t('common.typeOnboarding')
  const typeI18n = {
    type: typeLabel,
    typeArticle: t('common.articleDefFeminine'),
  }

  const {
    data: module,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['module', id],
    queryFn: () => moduleService.getModuleById(id!),
    enabled: !!id,
  })

  // Load progress data for all users in this module
  const { data: progressData = [], isLoading: progressLoading } = useQuery({
    queryKey: ['progress', 'module', id],
    queryFn: () => progressService.getModuleProgress(id!),
    enabled: !!id && !!user?.id,
  })

  // Load user history when modal is open
  const { data: userHistory = [] } = useQuery({
    queryKey: ['progress', 'user-history', selectedUserId],
    queryFn: () => progressService.getUserQuizHistory(selectedUserId!),
    enabled: !!selectedUserId && showHistoryModal,
  })

  // Use statistics from module response, fallback to calculated values if not available
  const totalUsers = module?.connectedUsers ?? progressData.length
  const videoWatchedCount =
    module?.videoWatchedCount ?? progressData.filter(p => p.videoWatched).length
  const quizCompletedCount =
    module?.quizCompletedCount ?? progressData.filter(p => p.quizCompleted).length
  const fullyCompletedCount =
    module?.fullyCompletedCount ?? progressData.filter(p => p.completed).length
  const averageScore =
    module?.averageScore ??
    (progressData.length > 0
      ? progressData.reduce((sum, p) => sum + p.quizScore, 0) / progressData.length
      : 0)

  const chapters = useMemo(
    () =>
      (module?.chapters || []).slice().sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)),
    [module]
  )

  const preferredLang = i18n.language?.split('-')[0] || 'de'

  // Helper functions
  const openHistoryModal = (userId: string) => {
    setSelectedUserId(userId)
    setShowHistoryModal(true)
  }

  const closeHistoryModal = () => {
    setShowHistoryModal(false)
    setSelectedUserId(null)
  }

  const selectedUser = (progressData as any).find((p: any) => p.userId === selectedUserId)?.user
  const selectedUserName = selectedUser
    ? `${selectedUser.firstName} ${selectedUser.lastName}`
    : ''

  return (
    <PageLayout
      headerProps={{
        title: module?.title || typeLabel,
        subtitle: module?.description || t('modules.detailSubtitle', typeI18n),
        variant: 'simple',
        showLogout: true,
      }}
      isLoading={isLoading || progressLoading}
      error={
        error ||
        (!module && !isLoading ? new Error(t('errors.loadModule', typeI18n)) : undefined)
      }
      loadingMessage={t('loading.module', typeI18n)}
      errorMessage={t('errors.loadModule', typeI18n)}
    >
      {module && (
        <Container>
          {/* Header Actions */}
          <div className='flex items-center justify-between mb-8'>
            <Button
              onClick={() => navigate(basePath)}
              variant='outline'
              className='flex items-center space-x-2'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              <span>{t('common.backToOverview')}</span>
            </Button>

            <div className='flex space-x-3'>
              <Button
                onClick={() => navigate(`${basePath}/${module.id}/edit`)}
                className='flex items-center space-x-2'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                  />
                </svg>
                <span>{t('common.edit')}</span>
              </Button>
              <Button
                onClick={() => navigate(`${basePath}/${module.id}/assign`)}
                variant='outline'
                className='flex items-center space-x-2'
              >
                <UserPlus className='w-4 h-4' />
                <span>{t('modules.assign')}</span>
              </Button>
            </div>
          </div>

          {/* Statistics Overview */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
            <ButtonBig title={t('modules.assignedEmployees')} value={totalUsers} icon={<User />} />
            <ButtonBig
              title={t('modules.videoWatched')}
              value={videoWatchedCount}
              subValue={`${totalUsers > 0 ? Math.round((videoWatchedCount / totalUsers) * 100) : 0}%`}
              icon={<Clock />}
            />
            <ButtonBig
              title={t('modules.quizCompleted')}
              value={quizCompletedCount}
              subValue={`${totalUsers > 0 ? Math.round((quizCompletedCount / totalUsers) * 100) : 0}%`}
              icon={<Video />}
            />
            <ButtonBig
              title={t('modules.averageScore')}
              value={`${Math.round(averageScore)}%`}
              subValue={t('modules.fullyCompleted', { count: fullyCompletedCount })}
              icon={<ChartNoAxesColumnIncreasing />}
            />
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Main Content */}
            <div className='lg:col-span-2 space-y-6'>
              {/* Chapters Overview with Quiz Previews */}
              {chapters.length > 0 && (
                <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
                  <h2 className='text-xl font-semibold mb-4 text-gray-900 '>
                    {t('modules.chapterOverview')}
                  </h2>
                  <div className='space-y-6'>
                    {chapters.map((ch: any, ci: number) => {
                      const chTitle =
                        (ch.translations?.[preferredLang]?.title as string | undefined) ||
                        t('common.chapterNumber', { number: ch.order ?? ci + 1 })
                      const chDesc = ch.translations?.[preferredLang]?.description as
                        | string
                        | undefined
                      const qList = (ch.questionsByLanguage?.[preferredLang] || []) as Array<{
                        question?: string
                        options?: string[]
                        correctAnswer?: number | string
                        explanation?: string
                      }>

                      return (
                        <div key={ch.id || ci} className='border border-gray-200 rounded-lg p-4'>
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <h3 className='text-lg font-semibold text-gray-900 '>{chTitle}</h3>
                              {chDesc && <p className='mt-1 text-sm text-gray-600 '>{chDesc}</p>}
                              {!chDesc && !ch.videoUrl && !ch.imageUrl && qList.length === 0 && (
                                <p className='mt-1 text-sm text-amber-600'>
                                  {t('modules.chapterContentMissing')}
                                </p>
                              )}
                            </div>
                            {(ch.videoUrl || ch.imageUrl) && (
                              <div className='w-48 ml-4 space-y-2'>
                                {ch.videoUrl && (
                                  <div className='aspect-video bg-gray-100 rounded overflow-hidden'>
                                    <video controls className='w-full h-full object-cover'>
                                      <source src={ch.videoUrl} type='video/mp4' />
                                    </video>
                                  </div>
                                )}
                                {ch.imageUrl && (
                                  <div className='aspect-video bg-gray-100 rounded overflow-hidden'>
                                    <img
                                      src={ch.imageUrl}
                                      alt={t('common.chapterNumber', { number: ch.order ?? ci + 1 })}
                                      className='w-full h-full object-contain'
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {qList.length > 0 && (
                            <div className='mt-4'>
                              <h4 className='text-sm font-medium text-gray-900 mb-2'>
                                {t('common.quizQuestions', { lang: preferredLang.toUpperCase() })}
                              </h4>
                              <div className='space-y-3'>
                                {qList.map((q, qi) => {
                                  const correctIdx =
                                    typeof q.correctAnswer === 'string'
                                      ? Number(q.correctAnswer)
                                      : q.correctAnswer
                                  return (
                                    <div key={qi} className='border border-gray-200 rounded-lg p-3'>
                                      <div className='font-medium text-gray-900 mb-2'>
                                        {t('common.questionWithText', {
                                          number: qi + 1,
                                          text: q.question,
                                        })}
                                      </div>
                                      <div className='space-y-1'>
                                        {(q.options || []).map((opt, oi) => (
                                          <div
                                            key={oi}
                                            className={`p-2 rounded ${
                                              oi === correctIdx
                                                ? 'bg-green-100 text-green-800 border border-green-200 '
                                                : 'bg-gray-50 text-gray-700 '
                                            }`}
                                          >
                                            {oi + 1}. {opt}
                                            {oi === correctIdx && (
                                              <span className='ml-2 text-xs font-medium'>✓</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className='space-y-6'>
              {/* Module Details */}
              <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
                <h3 className='text-lg font-semibold mb-4 text-gray-900 '>
                  {t('modules.moduleInfo', typeI18n)}
                </h3>
                <dl className='space-y-3'>
                  <div>
                    <dt className='text-sm font-medium text-gray-500 '>{t('common.status')}</dt>
                    <dd className='mt-1'>
                      <Badge
                        title={module.isActive ? t('common.active') : t('common.inactive')}
                        variant={module.isActive ? 'success' : 'error'}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className='text-sm font-medium text-gray-500 '>{t('common.created')}</dt>
                    <dd className='mt-1 text-sm text-gray-900 '>{formatDate(module.createdAt)}</dd>
                  </div>
                  <div>
                    <dd className='mt-1 text-sm text-gray-900 '>
                      {t('common.editedAt', { date: formatDate(module.updatedAt) })}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Employee Progress Table */}
              <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
                <h3 className='text-lg font-semibold mb-4 text-gray-900 '>
                  {t('modules.employeeProgress')}
                </h3>
                {progressData.length === 0 ? (
                  <p className='text-gray-500 text-sm'>{t('modules.noEmployeesAvailable')}</p>
                ) : (
                  <div className='space-y-3'>
                    {progressData.map((progress: any) => (
                      <div
                        key={progress.id}
                        className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'
                      >
                        <div className='flex-1'>
                          <p className='text-sm font-medium text-gray-900 '>
                            {progress.user?.firstName} {progress.user?.lastName}
                          </p>
                          <p className='text-xs text-gray-500 '>{progress.user?.email}</p>
                        </div>
                        <div className='flex items-center space-x-3'>
                          <div className='flex items-center space-x-2'>
                            <div
                              className={`w-2 h-2 rounded-full ${
                                progress.videoWatched ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                              title={
                                progress.videoWatched
                                  ? t('modules.videoWatched')
                                  : t('modules.videoNotWatched')
                              }
                            />
                            <div
                              className={`w-2 h-2 rounded-full ${
                                progress.quizCompleted ? 'bg-blue-500' : 'bg-gray-300'
                              }`}
                              title={
                                progress.quizCompleted
                                  ? t('modules.quizCompleted')
                                  : t('modules.quizNotCompleted')
                              }
                            />
                            <span className='text-xs text-gray-600 '>{progress.quizScore}%</span>
                          </div>
                          <Button
                            onClick={() => openHistoryModal(progress.userId)}
                            variant='outline'
                            size='sm'
                            className='text-xs px-2 py-1'
                          >
                            <svg
                              className='w-3 h-3 mr-1'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                              />
                            </svg>
                            {t('modules.quizHistory')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-xl shadow-xl w-full mx-4 max-h-[80vh] overflow-hidden'>
            <div className='flex items-center justify-between p-6 border-b border-gray-200 '>
              <h2 className='text-xl font-semibold text-gray-900 '>
                {t('modules.historyTitle', { name: selectedUserName })}
              </h2>
              <Button onClick={closeHistoryModal} variant='outline' size='sm'>
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </Button>
            </div>

            <div className='p-6 overflow-y-auto max-h-[60vh]'>
              {userHistory.length === 0 ? (
                <div className='text-center py-8'>
                  <svg
                    className='w-12 h-12 mx-auto text-gray-400 mb-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                    />
                  </svg>
                  <p className='text-gray-500 '>{t('modules.noQuizAttempts')}</p>
                </div>
              ) : (
                <div className='space-y-4'>
                  {userHistory.map((attempt: any, index: number) => (
                    <div
                      key={attempt.id || index}
                      className='border border-gray-200 rounded-lg p-4'
                    >
                      <div className='flex items-center justify-between mb-3'>
                        <div className='flex items-center space-x-3'>
                          <div
                            className={`w-3 h-3 rounded-full ${
                              attempt.passed ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          />
                          <span className='font-medium text-gray-900 '>#{index + 1}</span>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              attempt.passed
                                ? 'bg-green-100 text-green-800 '
                                : 'bg-red-100 text-red-800 '
                            }`}
                          >
                            {attempt.passed ? t('common.passed') : t('common.notPassed')}
                          </span>
                        </div>
                        <div className='text-sm text-gray-500 '>
                          {formatDate(attempt.completedAt)}{' '}
                          {formatTime(attempt.completedAt, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>

                      <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm'>
                        <div>
                          <span className='text-gray-500 '>{t('modules.score')}:</span>
                          <span className='ml-2 font-medium text-gray-900 '>
                            {attempt.correctAnswers}/{attempt.totalQuestions}
                          </span>
                        </div>
                        <div>
                          <span className='text-gray-500 '>{t('modules.averageScore')}:</span>
                          <span className='ml-2 font-medium text-gray-900 '>{attempt.score}%</span>
                        </div>
                        <div>
                          <span className='text-gray-500 '>{t('common.workTime')}:</span>
                          <span className='ml-2 font-medium text-gray-900 '>
                            {attempt.timeTaken
                              ? `${Math.floor(attempt.timeTaken / 60)}:${(attempt.timeTaken % 60)
                                  .toString()
                                  .padStart(2, '0')}`
                              : t('common.dash')}
                          </span>
                        </div>
                        <div>
                          <span className='text-gray-500 '>{t('common.defaultLanguage')}:</span>
                          <span className='ml-2 font-medium text-gray-900 '>
                            {attempt.language?.toUpperCase() || preferredLang.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Detailed answers */}
                      {attempt.answers && attempt.answers.length > 0 && (
                        <div className='mt-4 pt-4 border-t border-gray-200 '>
                          <h4 className='text-sm font-medium text-gray-900 mb-2'>
                            {t('common.question')}:
                          </h4>
                          <div className='space-y-2'>
                            {attempt.answers.map((answer: any, answerIndex: number) => (
                              <div
                                key={answerIndex}
                                className='flex items-center justify-between text-xs'
                              >
                                <span className='text-gray-600 '>
                                  {t('common.questionWithText', {
                                    number: answerIndex + 1,
                                    text: answer.question,
                                  })}
                                </span>
                                <div className='flex items-center space-x-2'>
                                  <span
                                    className={`px-2 py-1 rounded ${
                                      answer.isCorrect
                                        ? 'bg-green-100 text-green-800 '
                                        : 'bg-red-100 text-red-800 '
                                    }`}
                                  >
                                    {answer.selectedAnswer + 1}
                                  </span>
                                  {!answer.isCorrect && (
                                    <span className='text-gray-500 '>
                                      ({answer.correctAnswer + 1})
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
